import { useCallback } from 'react';
import debounce from 'lodash/debounce';

import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { InquisitiveActivitiesPageType } from 'global/graphql/scalars.type';
import { Dropdown } from 'approot/shared/dropdown/dropdown';
import { DropdownItemDescriptive } from 'approot/shared/dropdown/dropdown-item';
import ActivityBuilderInlineSvg from 'approot/shared/inline-svg/activity-builder/activity-builder-inline-svg';
import {
  ACTIVITIES_PAGE_TYPE_STUDENT,
  ACTIVITIES_PAGE_TYPE_TEACHER,
  ActivitiesComponentType,
  DRAFT_ACTION_UPDATE_PAGE_TYPE,
} from 'global/constants';
import { ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE } from '../activity-builder.constants';
import { useUpdateSection } from '../activity-builder-section.hooks';
import * as Styled from './activity-builder-page-type.style';
import IconPrint from 'approot/shared/icons/icon-print';

type Props = {
  id: string;
  pageType: InquisitiveActivitiesPageType | null;
  sort: string;
  contentId: number;
  contentType: ActivitiesComponentType;
};

const PageType = ({ id, pageType, contentId, sort, contentType }: Props) => {
  const { showError } = useSnackbar();

  const { updateSection } = useUpdateSection({
    contentId,
    contentType,
    onError: () => showError('There was an error updating the Slide'),
  });

  const handleOnChange = useCallback(
    (newType: InquisitiveActivitiesPageType) => {
      updateSection({
        contentType,
        contentId,
        sectionId: id,
        sort,
        pageType: newType,
        action: DRAFT_ACTION_UPDATE_PAGE_TYPE,
      });
    },
    [id, contentId, sort, contentType, updateSection]
  );

  const debounceChange = debounce(handleOnChange, 300);

  const currentType =
    pageType === null ? ACTIVITIES_PAGE_TYPE_TEACHER : pageType;

  return (
    <Styled.PageType className={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}>
      <Dropdown
        data-selector="page-type"
        aria-label="Change Page Type"
        buttonElement={
          currentType === ACTIVITIES_PAGE_TYPE_TEACHER
            ? 'Teacher Page'
            : 'Student Page'
        }
        dropdownContents={
          <>
            <DropdownItemDescriptive
              icon={<IconPrint width={20} height={20} />}
              primaryText="Student Page"
              secondaryText="Recommended for export PDF"
              onClick={() => debounceChange(ACTIVITIES_PAGE_TYPE_STUDENT)}
              data-selector="option-student-page"
            />
            <DropdownItemDescriptive
              icon={
                <ActivityBuilderInlineSvg
                  id="icon_present"
                  width={20}
                  height={20}
                />
              }
              primaryText="Teacher Page"
              secondaryText="Recommended for presenting"
              onClick={() => debounceChange(ACTIVITIES_PAGE_TYPE_TEACHER)}
              data-selector="option-teacher-page"
            />
          </>
        }
      />
    </Styled.PageType>
  );
};

export default PageType;
